import React, { useCallback, useEffect, useState } from "react";
import { getAssetUrl } from "../utils/asset";
import { getTenantId } from "../utils/tenant";

/**
 * S3Video Component
 *
 * A React component for displaying videos from S3 with proper authentication headers.
 * This component fetches videos with the required headers (X-Manager-ID, X-Config-ID, X-Tenant)
 * and displays them using blob URLs for proper authentication.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <S3Video keyPath="videos/tournament-intro.mp4" />
 *
 * // With fallback
 * <S3Video
 *   keyPath="videos/tournament-intro.mp4"
 *   fallbackSrc="/assets/default-video.mp4"
 * />
 * ```
 */

interface S3VideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  keyPath?: string | null;
  className?: string;
  fallbackSrc?: string;
  onError?: () => void;
  onLoad?: () => void;
}

interface VideoError {
  hasError: boolean;
  retryCount: number;
}

export const S3Video: React.FC<S3VideoProps> = ({
  keyPath,
  className = "",
  fallbackSrc = "/assets/placeholder.png",
  onError,
  onLoad,
  style,
  ...props
}) => {
  const [videoState, setVideoState] = useState<VideoError>({
    hasError: false,
    retryCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const assetUrl = useLoadVideo(keyPath);

  // Handle video load success
  const handleLoadedData = useCallback(() => {
    setIsLoading(false);
    setVideoState((prev) => ({ ...prev, hasError: false }));
    onLoad?.();
  }, [onLoad]);

  // Handle video load error
  const handleError = useCallback(() => {
    if (videoState.retryCount < 2) {
      // Retry loading the video
      setVideoState((prev) => ({
        hasError: false,
        retryCount: prev.retryCount + 1,
      }));
    } else {
      // Give up and show fallback
      setVideoState((prev) => ({ ...prev, hasError: true }));
      setIsLoading(false);
      onError?.();
    }
  }, [videoState.retryCount, onError]);

  // If no keyPath and no fallback, don't render
  if ((!keyPath && !fallbackSrc) || !assetUrl) {
    return null;
  }

  return (
    <div className={`relative ${className}`} style={style}>
      {isLoading && props?.placeholder && (
        <div className="absolute inset-0 flex items-center justify-center">
          {props?.placeholder}
        </div>
      )}

      <video
        src={assetUrl}
        className={`transition-opacity duration-300 ${
          isLoading ? "opacity-0" : "opacity-100"
        } ${className}`}
        onLoadedData={handleLoadedData}
        onError={handleError}
        style={{
          objectFit: "cover",
          objectPosition: "center",
          ...style,
        }}
        crossOrigin="anonymous"
        controls={false}
        autoPlay
        loop
        muted
        playsInline
        {...props}
      />

      {isLoading && !props?.placeholder && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="w-8 h-8 bg-gray-300 rounded"></div>
        </div>
      )}
    </div>
  );
};

export const useLoadVideo = (keyPath: string | undefined | null) => {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    const assetUrl = getAssetUrl(keyPath);
    if (!assetUrl) {
      return;
    }
    // Fetch the video
    fetch(assetUrl, {
      headers: {
        "X-Manager-ID": "tenant-web",
        "X-Tenant": getTenantId(),
      },
    })
      .then((res) => res.json())
      .then((data) => setVideoUrl(data.url));
  }, [keyPath]);

  return videoUrl;
};
